/**
 * Deep links into the CRM prospecting pages.
 *
 * The target pages read `?tab=<tab id>` to pick the active tab and
 * `?due=late|today|tomorrow` to preselect the matching date filter, so the
 * dashboard « Suivi des actions » widget can land on the exact list behind a
 * counter.
 */

export const CRM_TAB_PARAM = 'tab';
export const CRM_DUE_PARAM = 'due';

export type DueFilter = 'all' | 'late' | 'today' | 'tomorrow';

export const DUE_FILTER_OPTIONS: Array<{ value: DueFilter; label: string }> = [
  { value: 'all', label: 'Tous' },
  { value: 'late', label: 'En retard' },
  { value: 'today', label: 'Aujourd\'hui' },
  { value: 'tomorrow', label: 'Demain' },
];

export type OwnerCommunicationTabId = 'non-contacte' | 'en-attente' | 'a-appeler' | 'demandes-rappel';
export type MeetingTabId = 'a-contacter' | 'en-attente' | 'refus' | 'suivi-cloture';

export const buildCrmLink = (route: string, params: { tab?: string; due?: DueFilter } = {}): string => {
  const search = new URLSearchParams();
  if (params.tab) {
    search.set(CRM_TAB_PARAM, params.tab);
  }
  if (params.due && params.due !== 'all') {
    search.set(CRM_DUE_PARAM, params.due);
  }
  const query = search.toString();
  return query ? `${route}?${query}` : route;
};

export const parseCrmTab = <T extends string>(search: string, allowed: readonly T[]): T | null => {
  const value = new URLSearchParams(search).get(CRM_TAB_PARAM);
  return value !== null && (allowed as readonly string[]).includes(value) ? value as T : null;
};

export const parseDueFilter = (search: string): DueFilter => {
  const value = new URLSearchParams(search).get(CRM_DUE_PARAM);
  return value === 'late' || value === 'today' || value === 'tomorrow' ? value : 'all';
};

const toLocalDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

// Mirrors VigilanceService::getNextWorkingDay: « demain » skips the weekend.
const getNextWorkingDay = (date: Date): Date => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  while (next.getDay() === 0 || next.getDay() === 6) {
    next.setDate(next.getDate() + 1);
  }
  return next;
};

/**
 * API filters for a date field, using the same day boundaries as the dashboard
 * counters (VigilanceService::getCRMStatsFromCompany). Dates are sent as local
 * calendar days because the API stores and compares local date-times.
 *
 * @param prefix filter name without its After/Before suffix, e.g. `filterRecallAt`
 */
export const getDueFilters = (prefix: string, filter: DueFilter, now: Date = new Date()): Record<string, string> => {
  if (filter === 'all') {
    return {};
  }
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = getNextWorkingDay(today);
  if (filter === 'late') {
    return { [`${prefix}Before`]: toLocalDate(today) };
  }
  if (filter === 'today') {
    return { [`${prefix}After`]: toLocalDate(today), [`${prefix}Before`]: toLocalDate(tomorrow) };
  }
  return { [`${prefix}After`]: toLocalDate(tomorrow), [`${prefix}Before`]: toLocalDate(getNextWorkingDay(tomorrow)) };
};
